import ReactiveDialog from "../../common/ReactiveDialog";
import {
  Box,
  Typography,
  InputAdornment,
  FormControl,
  MenuItem,
  FormControlLabel,
} from "@mui/material";
import TextField from "components/common/TextField";
import Select from "components/common/Select";
import Checkbox from "components/common/Checkbox";
import {
  useAddHours,
  useGetFamilyDetails,
  useFetchInvoiceHistory,
} from "../../../services/advisor";
import Loading from "../../common/Loading";
import {
  Family,
  FetchInvoiceHistoryResponse_InvoiceMonth,
} from "protogen/advisors_service_pb";
import { useContext, useEffect, useState } from "react";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";

type Props = {
  familyRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpdate: (family: Family) => void;
};

type AddHoursContentProps = {
  family: Family;
  invoices: FetchInvoiceHistoryResponse_InvoiceMonth[];
  isAdmin: boolean;
  onHoursChange: (hours: number) => void;
  onPriceChange: (price: number) => void;
  onInvoiceChange: (invoiceRef: string) => void;
  onConfirmationChange: (confirmed: boolean) => void;
  hours: number;
  price: number;
  selectedInvoiceRef: string;
  confirmed: boolean;
};

const AddHoursContent = ({
  family,
  invoices,
  isAdmin,
  onHoursChange,
  onPriceChange,
  onInvoiceChange,
  onConfirmationChange,
  hours,
  price,
  selectedInvoiceRef,
  confirmed,
}: AddHoursContentProps) => {
  const totalCharge = price;
  const ratePerHour = hours > 0 ? totalCharge / hours : 0;

  const selectedInvoice = invoices.find(
    (inv) => inv.ref === selectedInvoiceRef,
  );
  const periodEndDateStr = selectedInvoice?.endDate || "";

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Typography variant="body">
        Adds extra hours to the <strong>{family.name}</strong> family and
        applies a one time charge.
      </Typography>
      {invoices.length > 1 && (
        <FormControl fullWidth>
          <Select
            variant="outlined"
            value={selectedInvoiceRef}
            onChange={(e) => onInvoiceChange(e.target.value as string)}
            label="Billing period"
          >
            {invoices.map((invoice) => (
              <MenuItem key={invoice.ref} value={invoice.ref}>
                {invoice.startDate} - {invoice.endDate}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <TextField
        label="Total charge"
        type="number"
        value={totalCharge}
        onChange={(e) => onPriceChange(Number(e.target.value))}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                margin: "0 0 0 20px",
                paddingTop: "16px",
              }}
            >
              $
            </InputAdornment>
          ),
        }}
        inputProps={{ min: 0, step: 10 }}
        sx={{
          "& .MuiInputBase-input": {
            padding: "0 0 0 6px",
          },
        }}
      />
      <TextField
        label="Number of hours"
        type="number"
        value={hours}
        onChange={(e) => onHoursChange(Number(e.target.value))}
        fullWidth
        inputProps={{ min: 0, step: 0.5 }}
      />
      <Box display="flex" flexDirection="column" gap="4px">
        <Typography variant="body" color="text.secondary">
          <strong>Rate per hour: ${ratePerHour.toFixed(2)}/hr</strong>
        </Typography>
        <Typography variant="body">
          The client will be charged immediately and the hours will be added to
          the billing period ending on {periodEndDateStr}.
        </Typography>
      </Box>
      {!isAdmin && (
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => onConfirmationChange(e.target.checked)}
            />
          }
          label="I confirm these changes have been communicated with the client"
        />
      )}
    </Box>
  );
};

export default ({ familyRef, open, setOpen, onUpdate }: Props) => {
  const {
    data: familyData,
    request: requestFamily,
    loading: loadingFamily,
  } = useGetFamilyDetails();
  const {
    data: invoiceData,
    request: requestInvoices,
    loading: loadingInvoices,
  } = useFetchInvoiceHistory();
  const [hours, setHours] = useState(1);
  const [price, setPrice] = useState(50);
  const [selectedInvoiceRef, setSelectedInvoiceRef] = useState<string>("");
  const [confirmed, setConfirmed] = useState(false);
  const { request: requestAddHours, loading: loadingAddHours } = useAddHours();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);

  const lastTwoInvoices =
    invoiceData?.invoices
      ?.slice()
      .sort((a, b) => Number(b.startDateSec) - Number(a.startDateSec))
      .slice(0, 2) || [];

  useEffect(() => {
    if (open) {
      requestFamily({ familyRef });
      requestInvoices({ familyRef });
      setConfirmed(false);
    }
  }, [open, familyRef]);

  useEffect(() => {
    if (lastTwoInvoices.length > 0 && !selectedInvoiceRef) {
      setSelectedInvoiceRef(lastTwoInvoices[0].ref);
    }
  }, [lastTwoInvoices, selectedInvoiceRef]);

  const onAddHours = async () => {
    if (loadingAddHours) return;
    if (!(hours && price && selectedInvoiceRef)) return;
    const resp = await requestAddHours({
      familyRef,
      hours,
      ammountCents: price * 100,
      invoiceRef: selectedInvoiceRef,
    });
    if (resp?.family) {
      onUpdate(resp.family);
      setOpen(false);
    }
  };

  return (
    <ReactiveDialog
      open={open}
      onClose={() => setOpen(false)}
      title="Add Extra Hours"
      primaryActionName="Add hours"
      primaryActionEnabled={
        !loadingAddHours &&
        !loadingFamily &&
        !loadingInvoices &&
        !!familyData?.family &&
        !!selectedInvoiceRef &&
        hours > 0 &&
        price > 0 &&
        (isAdmin || confirmed)
      }
      primaryAction={onAddHours}
      fullWidthSize="sm"
    >
      {loadingFamily || loadingInvoices ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loading />
        </Box>
      ) : familyData?.family && lastTwoInvoices.length > 0 ? (
        <AddHoursContent
          family={familyData.family}
          invoices={lastTwoInvoices}
          isAdmin={isAdmin}
          onHoursChange={setHours}
          onPriceChange={setPrice}
          onInvoiceChange={setSelectedInvoiceRef}
          onConfirmationChange={setConfirmed}
          hours={hours}
          price={price}
          selectedInvoiceRef={selectedInvoiceRef}
          confirmed={confirmed}
        />
      ) : null}
    </ReactiveDialog>
  );
};
